import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    message: String,
    type: String,
    duration: Number,
    position: String
  }

  connect() {
    console.debug("ToastController connected")
    // Auto-show toast if message is provided
    if (this.hasMessageValue && this.messageValue) {
      this.show(this.messageValue, this.typeValue)
    }
  }

  // Method to show toast programmatically
  show(message, type = 'is-success', duration = 3000) {
    const toast = this.createToast(message, type)
    this.addToPage(toast)
    this.setupAutoRemove(toast, duration)
    this.setupDeleteButton(toast)
  }

  // Method to show success toast
  showSuccess(message, duration = 3000) {
    this.show(message, 'is-success', duration)
  }

  // Method to show error toast
  showError(message, duration = 5000) {
    this.show(message, 'is-danger', duration)
  }

  // Method to show info toast
  showInfo(message, duration = 4000) {
    this.show(message, 'is-info', duration)
  }

  // Method to show warning toast
  showWarning(message, duration = 4000) {
    this.show(message, 'is-warning', duration)
  }

  createToast(message, type = 'is-success') {
    const toast = document.createElement('div')
    toast.setAttribute('data-turbo-temporary', '')
    toast.className = `notification ${type} toast-notification`
    toast.innerHTML = `
      <button class="delete" aria-label="delete"></button>
      ${message}
    `

    const position = this.hasPositionValue ? this.positionValue : 'top-right'
    this.styleToast(toast, position)

    return toast
  }

  styleToast(toast, position = 'top-right') {
    let positionStyles = ''

    switch(position) {
      case 'top-left':
        positionStyles = 'top: 20px; left: 20px;'
        break
      case 'top-center':
        positionStyles = 'top: 20px; left: 50%; transform: translateX(-50%);'
        break
      case 'top-right':
      default:
        positionStyles = 'top: 20px; right: 20px;'
        break
      case 'bottom-left':
        positionStyles = 'bottom: 20px; left: 20px;'
        break
      case 'bottom-center':
        positionStyles = 'bottom: 20px; left: 50%; transform: translateX(-50%);'
        break
      case 'bottom-right':
        positionStyles = 'bottom: 20px; right: 20px;'
        break
    }

    toast.style.cssText = `
      position: fixed;
      ${positionStyles}
      z-index: 3000;
      min-width: 200px;
      max-width: 400px;
      animation: slideIn 0.3s ease-out;
    `

    this.ensureAnimationsExist()
  }

  ensureAnimationsExist() {
    if (!document.querySelector('#toast-animations')) {
      const style = document.createElement('style')
      style.id = 'toast-animations'
      style.textContent = `
        @keyframes slideIn {
          from { transform: translateY(-100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes slideOut {
          from { transform: translateY(0); opacity: 1; }
          to { transform: translateY(-100%); opacity: 0; }
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOutRight {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
        @keyframes slideInLeft {
          from { transform: translateX(-100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOutLeft {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(-100%); opacity: 0; }
        }
        @keyframes slideInUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes slideOutDown {
          from { transform: translateY(0); opacity: 1; }
          to { transform: translateY(100%); opacity: 0; }
        }
      `
      document.head.appendChild(style)
    }
  }

  addToPage(toast) {
    document.body.appendChild(toast)
  }

  setupDeleteButton(toast) {
    const deleteButton = toast.querySelector('.delete')
    if (deleteButton) {
      deleteButton.addEventListener('click', () => {
        this.removeToast(toast)
      })
    }
  }

  setupAutoRemove(toast, duration) {
    setTimeout(() => {
      this.removeToast(toast)
    }, duration)
  }

  removeToast(toast) {
    if (toast && toast.parentNode) {
      toast.style.animation = 'slideOut 0.3s ease-in forwards'
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast)
        }
      }, 300)
    }
  }

  // Static method to create toast from anywhere in the app
  static show(message, type = 'is-success', duration = 3000, position = 'top-right') {
    const toastController = new this()
    toastController.positionValue = position
    toastController.show(message, type, duration)
  }

  // Static convenience methods
  static showSuccess(message, duration = 3000) {
    this.show(message, 'is-success', duration)
  }

  static showError(message, duration = 5000) {
    this.show(message, 'is-danger', duration)
  }

  static showInfo(message, duration = 4000) {
    this.show(message, 'is-info', duration)
  }

  static showWarning(message, duration = 4000) {
    this.show(message, 'is-warning', duration)
  }
}
